/**
 * Hero Section Component
 * Modern hero with entrance animations and scroll effects
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { HERO } from '../../constants/images';
import { stats } from '../../data/mock';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        backgroundImage: `url(${HERO.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: isMobile ? 'scroll' : 'fixed',
      }}
    >
      {/* Gradient overlay - balanced for text visibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/85 via-[#1A1A1A]/75 to-[#1A1A1A]/65" />

      {/* Decorative top line with subtle animation */}
      <motion.div
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B7355] via-[#9A8468] to-transparent"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformOrigin: 'left' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 sm:py-24 md:py-32">
        <div className="max-w-3xl">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 sm:space-y-8"
          >
            <motion.span
              variants={itemVariants}
              className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#8B7355]/20 text-[#8B7355] text-[10px] sm:text-xs font-medium tracking-widest uppercase rounded-full border border-[#8B7355]/30"
            >
              27+ Years of Excellence
            </motion.span>

            <motion.h1
              variants={itemVariants}
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-semibold leading-[1.1] tracking-tight"
            >
              Building the future,
              <br />
              one <span className="text-[#8B7355]">project</span> at a time
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-[#E8E8E0]/80 text-base sm:text-lg md:text-xl max-w-xl leading-relaxed"
            >
              Innovators in industrial construction. We craft bold, inspired structures with precision and expertise.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 pt-2 sm:pt-4"
            >
              <Link
                to="/contact"
                className="group relative px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-semibold tracking-wider text-[#1A1A1A] bg-white hover:bg-[#8B7355] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 overflow-hidden"
                data-testid="hero-cta-contact"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                GET IN TOUCH
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/projects"
                className="group px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-semibold tracking-wider text-white border-2 border-white/30 hover:border-[#8B7355] hover:bg-[#8B7355]/10 transition-all duration-300 text-center"
                data-testid="hero-cta-projects"
              >
                VIEW PROJECTS
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Track Record Marquee */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-gradient-to-t from-[#1A1A1A]/95 to-transparent"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <h3 className="text-left text-white/70 text-[10px] sm:text-xs md:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4">
            Numbers that speak for excellence
          </h3>

          <div className="relative overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap">
              {[...stats, ...stats, ...stats].map((stat, index) => (
                <div
                  key={`marquee-${index}`}
                  className="flex items-center gap-4 sm:gap-6 md:gap-8 px-4 sm:px-6 md:px-10 flex-shrink-0"
                >
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-white">
                      {stat.value}
                    </div>
                    <div className="text-[#8A8884] text-[10px] sm:text-xs md:text-sm mt-1 whitespace-nowrap">
                      {stat.label}
                    </div>
                  </div>
                  <div className="w-px h-8 sm:h-10 md:h-12 bg-white/20" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
